import Tabs from "@mui/material/Tabs";
import {a11yStandardKeys, unitTypeKeys} from "../../../constants/constants";
import React, {FC, useMemo} from "react";
import styled from "styled-components";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import {A11yStandardType} from "../../../models/A11yStandard";
import {useTranslation} from "react-i18next";
import HomeIcon from '@mui/icons-material/Home';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import AccessibilityToiletSvg from "../../../assets/svg/AccessibilityToiletSvg";
import ExhibitionSvg from "../../../assets/svg/ExhibitionSvg";
import MeetingRoomSvg from "../../../assets/svg/MeetingRoomSvg";
import HotelRoomSvg from "../../../assets/svg/HotelRoomSvg";
import AttractionSvg from "../../../assets/svg/AttractionSvg";
import KioskSvg from "../../../assets/svg/KioskSvg";
import RestaurantSvg from "../../../assets/svg/RestaurantSvg";
import {AccessibilityUnit} from "../../../models/AccessibilityUnits";
import {
  BUILDING_INFO_TAB,
  PropertyPageTabKey,
  SHOPS_TAB,
  UNIT_LIST_TABS,
} from "../../../constants/propertyPageTabs";

const StyledBox = styled(Box)`
  display: grid;
  margin-top: 5px;
`;

const StyledTab = styled(Tab)`
  font-size: 14px !important;
  min-height: 25px;
  min-width: 40px;
  color: #000000DE !important;
  display: flex;
  flex-direction: row;
  gap: 8px;
  &:hover {
    color: #000000DE !important;
    background-color: transparent !important;
  }
  &.Mui-selected {
    color: #1976d2 !important;
  }
  .MuiTab-iconWrapper {
    margin: 0 !important;
  }
  @media (max-width: 768px) {
    .label {
      display: none;
    }
  }
`;

const UNIT_LIST_ICONS: Partial<Record<unitTypeKeys, JSX.Element>> = {
  [unitTypeKeys.hotelRoomType]: <HotelRoomSvg />,
  [unitTypeKeys.meetingRoom]: <MeetingRoomSvg />,
  [unitTypeKeys.exhibition]: <ExhibitionSvg />,
  [unitTypeKeys.museumShop]: <ShoppingBasketIcon />,
  [unitTypeKeys.attraction]: <AttractionSvg />,
  [unitTypeKeys.restaurant]: <RestaurantSvg />,
  [unitTypeKeys.mannedKiosk]: <KioskSvg />,
  [unitTypeKeys.toiletRoomCommon]: <AccessibilityToiletSvg />,
}

type PropsType = {
  value: PropertyPageTabKey
  handleChange: (tab: PropertyPageTabKey) => void
  display: boolean
  a11yStandard: A11yStandardType,
  accessibleUnits: AccessibilityUnit[];
}

const TabsBlock: FC<PropsType> = ({
  value,
  handleChange,
  display,
  a11yStandard,
  accessibleUnits,
}) => {
  const { t } = useTranslation()

  const unitTypes = useMemo(() => {
    return new Set(accessibleUnits.map(({ type }) => type));
  }, [accessibleUnits]);

  const tabs = [
    {
      key: BUILDING_INFO_TAB,
      label: t("header_buildingInformation"),
      icon: <HomeIcon />,
      visible: true,
    },
    {
      key: SHOPS_TAB,
      label: t("property_tabTitle_shops"),
      icon: <ShoppingBasketIcon />,
      visible: a11yStandard.key === a11yStandardKeys.shoppingMall && display,
    },
    ...UNIT_LIST_TABS.map(({unitType, labelKey}) => ({
      key: unitType,
      label: t(labelKey),
      icon: UNIT_LIST_ICONS[unitType],
      visible: unitTypes.has(unitType),
    })),
  ];

  if (tabs.every((i) => !i.visible)) return null;

  return (
    <StyledBox sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}>
      <Tabs
        variant="scrollable"
        value={value}
        onChange={(event: React.SyntheticEvent, tab: PropertyPageTabKey) => handleChange(tab)}
        aria-label="tabs"
        textColor="inherit"
        allowScrollButtonsMobile
      >
        {tabs
          .filter((tab) => tab.visible)
          .map(({ key, label, icon }) => (
            <StyledTab
              key={key}
              value={key}
              icon={icon}
              label={<span className="label">{label}</span>}
            />
          ))}
      </Tabs>
    </StyledBox>
  );
};

export default TabsBlock
